import { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  FileText, Check, X, Edit2, Eye, Users, Search,
  Settings, Plus, Trash2, Upload, Image, Building2,
  List, LogOut, Menu, ChevronDown,
} from 'lucide-react';
import SigningModal from './signing/SigningModal';
import { useAuth } from '../contexts/AuthContext';
import { useConsentFlow } from '../contexts/ConsentFlowContext';
import { clinics as clinicsApi } from '../api/client';
import { PROFESSION_OPTIONS, getDisplayName } from '../lib/profession';

export default function AppLayout() {
  const navigate = useNavigate();
  const { user: authUser, clinics, currentClinic, switchClinic, logout, refreshClinics } = useAuth();
  const {
    appState, updateUser, saveProfile,
    clinicSettings, setClinicSettings, updateClinicSetting,
    selectedProcedures, patientData, selectedPatient, procedures,
    showSigningModal, setShowSigningModal,
  } = useConsentFlow();

  const activeClinic = currentClinic || clinics[0];

  // UI state
  const [showClinicSwitcher, setShowClinicSwitcher] = useState(false);
  const [showClinicSettings, setShowClinicSettings] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showAddClinicModal, setShowAddClinicModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const logoInputRef = useRef(null);

  // Sync clinic settings when modal opens
  useEffect(() => {
    if (showClinicSettings && activeClinic) {
      setClinicSettings({
        name: activeClinic.name || '',
        address: activeClinic.address || '',
        phone: activeClinic.phone || '',
        logoUrl: activeClinic.logoUrl || '',
      });
    }
  }, [showClinicSettings]);

  const addNewClinic = () => { setShowClinicSwitcher(false); setShowAddClinicModal(true); };

  const deleteClinic = (clinicId) => {
    if (clinics.length <= 1) { toast.error('Você precisa ter pelo menos uma clínica.'); return; }
    if (confirm('Tem certeza que deseja excluir esta clínica? Esta ação não pode ser desfeita.')) {
      // Note: deleteClinic via API not yet implemented — this was modifying local appState only
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => { setClinicSettings(prev => ({ ...prev, logoUrl: reader.result })); };
      reader.readAsDataURL(file);
    }
  };

  const clearLogo = () => {
    setClinicSettings(prev => ({ ...prev, logoUrl: '' }));
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const navItems = [
    { to: '/', label: 'Documentos', icon: List, end: true },
    { to: '/patients', label: 'Pacientes', icon: Users },
    { to: '/builder', label: 'Construir', icon: Edit2 },
    { to: '/preview', label: 'Visualizar', icon: Eye, disabled: selectedProcedures.length === 0 },
    { to: '/templates', label: 'Modelos', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <FileText className="w-7 h-7 sm:w-8 sm:h-8 text-indigo-600 flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                  <span className="sm:hidden">DocTerms</span>
                  <span className="hidden sm:inline">Sistema de Termos de Consentimento</span>
                </h1>
                <p className="text-xs sm:text-sm text-gray-600">Oftalmologia</p>
              </div>
            </div>

            <div className="flex items-center space-x-1 sm:space-x-3">
              {/* Clinic Switcher */}
              <div className="relative">
                <button
                  onClick={() => setShowClinicSwitcher(!showClinicSwitcher)}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors min-w-[44px] min-h-[44px]"
                >
                  <Building2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span className="text-sm font-medium max-w-[120px] sm:max-w-64 truncate hidden xs:inline">
                    {activeClinic?.name || 'Selecionar Clínica'}
                  </span>
                  <ChevronDown className="w-4 h-4 flex-shrink-0" />
                </button>

                {showClinicSwitcher && (
                  <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-2">
                      <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">Suas Clínicas</p>
                      {clinics.map(clinic => (
                        <button
                          key={clinic.id}
                          onClick={() => switchClinic(clinic.id)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-md ${
                            clinic.id === currentClinic?.id ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-2 min-w-0">
                            {clinic.logoUrl ? (
                              <img src={clinic.logoUrl} alt="" className="w-6 h-6 object-contain rounded flex-shrink-0" />
                            ) : (
                              <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center flex-shrink-0">
                                <Building2 className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                            <span className="text-sm font-medium truncate">{clinic.name || 'Sem nome'}</span>
                          </div>
                          {clinic.id === currentClinic?.id && <Check className="w-4 h-4 text-indigo-600 flex-shrink-0 ml-2" />}
                        </button>
                      ))}
                      <hr className="my-2" />
                      <button onClick={addNewClinic} className="w-full flex items-center space-x-2 px-3 py-2 text-indigo-600 hover:bg-indigo-50 rounded-md">
                        <Plus className="w-4 h-4" />
                        <span className="text-sm font-medium">Adicionar Clínica</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Desktop action buttons */}
              <div className="hidden sm:flex items-center space-x-1">
                <button onClick={() => setShowClinicSettings(true)} className="flex items-center px-3 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors min-w-[44px] min-h-[44px]" title="Configurações da Clínica">
                  <Settings className="w-5 h-5" />
                </button>
                <button onClick={() => setShowUserSettings(true)} className="flex items-center px-3 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors min-w-[44px] min-h-[44px]" title="Meu Perfil">
                  <Users className="w-5 h-5" />
                </button>
                <button onClick={logout} className="flex items-center px-3 py-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors min-w-[44px] min-h-[44px]" title="Sair">
                  <LogOut className="w-5 h-5" />
                </button>
              </div>

              {/* Mobile hamburger */}
              <div className="relative sm:hidden">
                <button onClick={() => setShowMobileMenu(!showMobileMenu)} className="flex items-center justify-center p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors min-w-[44px] min-h-[44px]">
                  <Menu className="w-5 h-5" />
                </button>
                {showMobileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="py-1">
                      <button onClick={() => { setShowClinicSettings(true); setShowMobileMenu(false); }} className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50">
                        <Settings className="w-5 h-5 text-gray-400" /><span className="text-sm font-medium">Configurações da Clínica</span>
                      </button>
                      <button onClick={() => { setShowUserSettings(true); setShowMobileMenu(false); }} className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50">
                        <Users className="w-5 h-5 text-gray-400" /><span className="text-sm font-medium">Meu Perfil</span>
                      </button>
                      <hr className="my-1" />
                      <button onClick={() => { logout(); setShowMobileMenu(false); }} className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50">
                        <LogOut className="w-5 h-5" /><span className="text-sm font-medium">Sair</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-4 sm:mt-6 print:hidden">
        <div className="flex space-x-1 sm:space-x-2 border-b border-gray-200 overflow-x-auto">
          {navItems.map(({ to, label, icon: Icon, disabled, end }) => (
            <NavLink
              key={to}
              to={disabled ? '#' : to}
              end={end}
              onClick={(e) => disabled && e.preventDefault()}
              className={({ isActive }) =>
                `px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-medium whitespace-nowrap ${
                  disabled ? 'text-gray-400 cursor-not-allowed' :
                  isActive ? 'border-b-2 border-indigo-600 text-indigo-600' : 'text-gray-600 hover:text-gray-900'
                }`
              }
            >
              <Icon className="w-4 h-4 inline mr-1 sm:mr-2" />
              {label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Page Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Outlet />
      </div>

      {/* Clinic Settings Modal */}
      {showClinicSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center"><Building2 className="w-5 h-5 mr-2" />Configurações da Clínica</h2>
                <button onClick={() => setShowClinicSettings(false)} className="text-gray-500 hover:text-gray-700"><X className="w-6 h-6" /></button>
              </div>
              <div className="space-y-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Logo da Clínica</label>
                  <div className="flex items-start space-x-4">
                    {clinicSettings.logoUrl ? (
                      <div className="relative">
                        <img src={clinicSettings.logoUrl} alt="Logo preview" className="w-24 h-24 object-contain border border-gray-300 rounded-lg" />
                        <button onClick={clearLogo} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400"><Image className="w-8 h-8" /></div>
                    )}
                    <div className="flex-1">
                      <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="logo-upload" />
                      <button type="button" onClick={() => logoInputRef.current?.click()} className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />{clinicSettings.logoUrl ? 'Trocar Logo' : 'Carregar Logo'}
                      </button>
                      <p className="text-xs text-gray-500 mt-2">Recomendado: imagem quadrada, PNG ou JPG, máx 500KB</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome da Clínica</label>
                  <input type="text" value={clinicSettings.name} onChange={(e) => updateClinicSetting('name', e.target.value)} placeholder="Ex: Clínica Oftalmológica Visão Clara" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Endereço</label>
                  <input type="text" value={clinicSettings.address} onChange={(e) => updateClinicSetting('address', e.target.value)} placeholder="Ex: SGAS 915, Bloco C, Sala 123" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Cidade</label>
                    <input type="text" value={clinicSettings.city} onChange={(e) => updateClinicSetting('city', e.target.value)} placeholder="Ex: Brasília" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Estado (UF)</label>
                    <input type="text" value={clinicSettings.state} onChange={(e) => updateClinicSetting('state', e.target.value.toUpperCase().slice(0, 2))} placeholder="Ex: DF" maxLength={2} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Telefone</label>
                  <input type="text" value={clinicSettings.phone} onChange={(e) => updateClinicSetting('phone', e.target.value)} placeholder="Ex: (61) 3333-4444" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                {/* Preview */}
                {(clinicSettings.name || clinicSettings.logoUrl) && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Pré-visualização do Cabeçalho</label>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center">
                        {clinicSettings.logoUrl && <img src={clinicSettings.logoUrl} alt="Logo" className="w-16 h-16 object-contain mr-4" />}
                        <div>
                          {clinicSettings.name && <p className="font-bold text-gray-900">{clinicSettings.name}</p>}
                          {clinicSettings.address && <p className="text-sm text-gray-600">{clinicSettings.address}</p>}
                          {(clinicSettings.city || clinicSettings.state) && <p className="text-sm text-gray-600">{[clinicSettings.city, clinicSettings.state].filter(Boolean).join(' - ')}</p>}
                          {clinicSettings.phone && <p className="text-sm text-gray-600">{clinicSettings.phone}</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-between">
                <button onClick={() => { deleteClinic(currentClinic?.id); setShowClinicSettings(false); }} className="px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100" disabled={clinics.length <= 1} title={clinics.length <= 1 ? 'Você precisa ter pelo menos uma clínica' : 'Excluir esta clínica'}>
                  <Trash2 className="w-4 h-4 inline mr-2" />Excluir Clínica
                </button>
                <div className="flex space-x-3">
                  <button onClick={() => { setClinicSettings({ name: '', address: '', city: '', state: '', phone: '', logoUrl: '' }); if (logoInputRef.current) logoInputRef.current.value = ''; }} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Limpar Tudo</button>
                  <button onClick={() => setShowClinicSettings(false)} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
                  <button onClick={async () => {
                    if (!currentClinic?.id) return;
                    try {
                      await clinicsApi.update(currentClinic.id, {
                        name: clinicSettings.name || undefined, address: clinicSettings.address || undefined,
                        city: clinicSettings.city || undefined, state: clinicSettings.state || undefined,
                        phone: clinicSettings.phone || undefined, logoUrl: clinicSettings.logoUrl || undefined,
                      });
                      await refreshClinics();
                      setShowClinicSettings(false);
                    } catch (err) {
                      console.error('Failed to save clinic settings:', err);
                      toast.error(`Erro ao salvar: ${err.message || 'Tente novamente.'}`);
                    }
                  }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Salvar</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Profile Modal */}
      {showUserSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center"><Users className="w-5 h-5 mr-2" />Meu Perfil</h2>
                <button onClick={() => setShowUserSettings(false)} className="text-gray-500 hover:text-gray-700"><X className="w-6 h-6" /></button>
              </div>
              <div className="space-y-4">
                {authUser?.email && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                    <p className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">{authUser.email}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome Completo</label>
                  <input type="text" value={appState.user.name} onChange={(e) => updateUser('name', e.target.value)} placeholder="Ex: Maria Silva" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Função</label>
                  <select value={appState.user.profession || ''} onChange={(e) => updateUser('profession', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Selecione...</option>
                    {PROFESSION_OPTIONS.map((opt) => <option key={opt.code} value={opt.code}>{opt.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">CRM</label>
                  <input type="text" value={appState.user.crm} onChange={(e) => updateUser('crm', e.target.value)} placeholder="Ex: CRM DF 23827" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">Estas informações serão usadas em todos os termos de consentimento que você gerar, independente da clínica selecionada.</p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button onClick={() => setShowUserSettings(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={() => { saveProfile(); setShowUserSettings(false); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Salvar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Clinic Modal */}
      {showAddClinicModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Adicionar Nova Clínica</h2>
                <button onClick={() => setShowAddClinicModal(false)} className="text-gray-500 hover:text-gray-700"><X className="w-6 h-6" /></button>
              </div>
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4"><Building2 className="w-8 h-8 text-indigo-600" /></div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Em Breve!</h3>
                <p className="text-gray-600 mb-4">A funcionalidade de adicionar novas clínicas estará disponível em breve. Entre em contato conosco para expandir sua conta.</p>
                <div className="bg-gray-50 rounded-lg p-4 text-left">
                  <p className="text-sm font-medium text-gray-700 mb-2">Para adicionar clínicas:</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>contato@exemplo.com.br</li>
                    <li>(61) 99999-9999</li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button onClick={() => setShowAddClinicModal(false)} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">Entendi</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close clinic switcher */}
      {showClinicSwitcher && <div className="fixed inset-0 z-40" onClick={() => setShowClinicSwitcher(false)} />}

      {/* Digital Signing Modal */}
      <SigningModal
        isOpen={showSigningModal}
        onClose={() => setShowSigningModal(false)}
        clinicId={currentClinic?.id}
        consentData={{
          patientId: patientData.patientId || undefined,
          patientName: patientData.name,
          patientCpf: patientData.cpf,
          patientEmail: selectedPatient?.email || patientData.email || '',
          patientPhone: selectedPatient?.phone || patientData.phone || '',
          procedures: selectedProcedures.map(id => procedures[id]?.name).filter(Boolean),
          consultationDate: patientData.consultDate,
        }}
        onSuccess={(consent) => { console.log('Consent signed:', consent); }}
      />
    </div>
  );
}
